/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-destructuring */
import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import once from 'lodash/once';
import '@tencent/dui/lib/utils/polyfill';
import '@tencent/dui-mobile/lib/utils/polyfill';
import {
    ClientReady,
    LiveState,
    LiveStateChanged,
    PageChanged,
    AllowFlipState,
    AllowFlipChanged,
    RoomNewInfoChanged,
    LiveRoleType,
    FallbackToPolling,
    LiveSocketShutDown,
} from '@tencent/live-components';
import { tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import Modal from '@tencent/dui/lib/components/Modal';
import i18n from '@i18next/i18n';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { openUrlWithCheck, getRedirectUrl, initScriptLoader } from '@src/js/common/utils';
import TimeRecord from '@src/slide/infrastructure/module/independent/time-record';
import { TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import ua from '@tencent/docs-user-agent';
import {
    RoomAction,
    PlayerAction,
    MEETING_SCENE_SOURCE,
    ROLE,
    DOC_TYPE,
    MEETING_ROOM_ACTION,
} from '../../message_const';
import LiveBar from '../live-bar';
import LaserBoardContainer from '../laser-board-container';
import FollowBtn from '@src/framework/frame/components/editing-present-follow-btn/follow-btn';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import APP_OFFICIAL_ACCOUNT from '@tencent/docs-design-resources/mobile/png@3x/app_official_account@3x.png';
import { IPlayerState } from '../../common.interface';
import { MeetingRoomHelper } from '../../roomEventHandler';
import MeetingRoomReport from '../../utils/report';
import FullscreenHandler, { FullscreenEventsKeys } from '@utils/fullscreen/fullscreen-handler';
import MeetingEventBus from '@src/page/toc/common/meeting-room-manager/components/eventBus';
import { MeetingRoomEvents } from '@src/page/toc/common/meeting-room-manager/components/meeting-room/meeting-room-interface';
import CommonTips, { CommonTipsActionType, CommonTipsType } from '@com/common-tips';
import { ILiveManager } from '@src/slide/application/service/live-manager/core/interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

interface IPlayingContainerProps {
    role: ROLE;
    docType: DOC_TYPE;
    scenceEditorRemote: boolean;
    docId: string;
    roomId: string;
    padId: string;
    currentPage: number;
    playerShow: boolean;
    sceneSource: MEETING_SCENE_SOURCE;
    handleScenceEditorEndPlay?: (pageIndex?: number) => void;
    meetingRoomStateChange: <T extends MEETING_ROOM_ACTION>(
        action: T,
        ...addition: Parameters<MeetingRoomEvents[T]>
    ) => void;
    changeLoadingText: (showLoading: boolean, loadingText?: string) => void;
    showRoomClose: () => void;
    showRoomError: (...args: any[]) => void;
    meetingRoomReport: MeetingRoomReport;
    liveManager: ILiveManager;
}

interface IPlayingContainerStates {
    playerState: IPlayerState;
    isFullscreen: boolean;
}

type Task<T = unknown> = Promise<T> & { resolve: () => void };

/** Promise with resolve (self) */
function createTask<T = unknown>(): Task<T> {
    let resolve;
    const promise = new Promise(res => resolve = res) as Task<T>;
    promise.resolve = resolve;

    return promise;
}

export default class PlayingContainer extends Component<IPlayingContainerProps, IPlayingContainerStates> {
    private mobileToolBarHiddenTimer: ReturnType<typeof setTimeout> | null;
    private meetingRoomHelper: MeetingRoomHelper;

    /** 准备 player */
    private taskPreparePlayer = createTask();
    /** 准备 房间层 socket */
    private taskPrepareSocket = createTask();
    /** 房间层直播是否已经开始过，用以区分初次开始 or 主讲人暂停后恢复 */
    private hasLiveBeenOnAir = false;
    /** 注册事件的销毁函数集合 */
    private eventsOffCallbacks: Function[] = [];

    public constructor(props: IPlayingContainerProps) {
        super(props);
        this.state = {
            playerState: {
                currentPage: 0,
                currentFragment: 0,
                pageCount: 0,
                laserTurnOn: false,
                scale: 1.0,
                scrollOffset: 0,
            },
            isFullscreen: FullscreenHandler.getInstance().isFullscreen,
        };
        this.initLiveManager();
        initScriptLoader();

        // 限定远程演示房间
        if (!this.props.scenceEditorRemote) {
            this.initMeetingRoomSocketWhenUserIdExist();
            this.waitForShowPlayer();
        }
    }

    public componentDidMount() {
        this.handleFullScreenChangeForAllBrowser(document.addEventListener, this.fullScreenChange);
        FullscreenHandler.getInstance().event.on(FullscreenEventsKeys.FULLSCREEN_STATUS_CHANGE, this.fullScreenChange);
        this.initLaserFeatureInSlide();
    }

    public componentWillUnmount() {
        this.handleFullScreenChangeForAllBrowser(document.removeEventListener, this.fullScreenChange);
        FullscreenHandler.getInstance().event.off(FullscreenEventsKeys.FULLSCREEN_STATUS_CHANGE, this.fullScreenChange);
        this.eventsOffCallbacks.forEach(unbind => unbind());
    }

    public render() {
        if (!this.props.playerShow) return null;

        return (
            <>
                {this.renderLaserBoard()}
                {this.renderLiveBar()}
                {this.renderFollowBtn()}
            </>
        );
    }

    // Slide laser 改动，激光笔逻辑区别于其他品类
    private initLaserFeatureInSlide() {
        if (this.props.docType !== DOC_TYPE.SLIDE) return;

        const loop = () => {
            if (this.props.liveManager.isAudience) {
                const pen = this.props.liveManager.getRemotePoint();
                if (pen) {
                    MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteLaserMove, { laserInfo: pen });
                }
            }

            requestAnimationFrame(loop);
        };

        requestAnimationFrame(loop);
    }

    // 编辑态远程演示，Player准备完毕
    private onEditorRemotePlayerReady() {
        const { meetingRoomStateChange } = this.props;
        this.preparePlayerContent();
        meetingRoomStateChange(MEETING_ROOM_ACTION.ROOM_READY);
        // 主人开始演示
        window.log?.info('[RemoteShow]: onEditorRemotePlayerReady, liveBegin');
        this.props.liveManager.liveBegin(true);
        // 打开激光笔
        MeetingEventBus.sendRoomEvent2Player(RoomAction.LaserTrunOn);
    }

    // MeetingRoom 场景下，Player准备完毕
    private onMeetingRoomPlayerReady() {
        this.preparePlayerContent();
        this.taskPreparePlayer.resolve();
    }

    private initMeetingRoomSocketWhenUserIdExist() {
        if (this.props.liveManager.userId) {
            this.initMeetingRoomSocket();
            return;
        }

        setTimeout(() => {
            this.initMeetingRoomSocketWhenUserIdExist();
        }, 500);
    }

    /** 初始化房间层 socket */
    private initMeetingRoomSocket() {
        const { role, roomId, scenceEditorRemote, meetingRoomReport } = this.props;
        if (role === ROLE.MASTER) {
            // 进房间
            window.log?.info('[RemoteShow]: onMeetingRoomPlayerReady, liveManager.createShow');
            this.props.liveManager.createShow((result) => {
                if (result.error) {
                    window.log?.error('[RemoteShow]: onMeetingRoomPlayerReady, createShow,error:', result.error);
                    Modal.confirm({
                        content: i18n.t('信息未成功拉取，请刷新后重新发起远程演示。'),
                        onOk: () => {
                            tencentDocOpenUrlReload();
                        },
                        okText: i18n.t('立即刷新'),
                        cancelText: null,
                    });
                } else {
                    setTimeout(() => {
                        // 主人态进入房间，完成后 ClientReady
                        window.log?.info('[RemoteShow]: onMeetingRoomPlayerReady, liveManager.masterEnter');
                        meetingRoomReport.reportPerformanceNowNotEditor(scenceEditorRemote, 'masterEnter');
                        this.props.liveManager.masterEnter();
                    }, 0);
                }
            });
        } else if (role === ROLE.AUDIENCE) {
            // 观众态进入房间，完成后 ClientReady
            window.log?.info('[RemoteShow]: onMeetingRoomPlayerReady, liveManager.audienceEnter', roomId);
            meetingRoomReport.reportPerformanceNowNotEditor(scenceEditorRemote, 'audienceEnter');
            this.props.liveManager.audienceEnter(roomId);
        }
    }

    // MeetingRoom场景下成功进入演示房间，演示者/观众
    private onMeetingRoomClientReady = () => {
        const { role } = this.props;
        TimeRecord.end(TDWActionType.REMOTESHOW_ENTERROOM);
        if (role === ROLE.MASTER) {
            this.taskPrepareSocket.resolve();
            // 主人开始演示
            window.log?.info('[RemoteShow]: onMeetingRoomClientReady, liveManager.liveBegin');
            this.props.liveManager.liveBegin(false);
        }
    };

    // 通知Player准备数据
    private preparePlayerContent() {
        const { currentPage } = this.props;
        MeetingEventBus.sendRoomEvent2Player(RoomAction.ShowPlayerContent, {
            currentPage,
            currentRev: this.props.liveManager.liveInfo.revision,
        });
    }

    private showPlayerFrame() {
        this.props.meetingRoomStateChange(MEETING_ROOM_ACTION.ROOM_READY);
    }

    private sendLiveBegin() {
        MeetingEventBus.sendRoomEvent2Player(RoomAction.LiveBegin, {
            currentRev: this.props.liveManager.liveInfo.revision,
            currentPage: this.props.liveManager.liveInfo.page || 0,
            currentFragment: this.props.liveManager.liveInfo.fragment || 0,
        });
        const allowFlip = this.props.liveManager.allowFlip();
        if (allowFlip) {
            MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteAllowFlipOn);
        } else {
            MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteAllowFlipOff);
        }
    }

    private initLiveManager() {
        const { docId } = this.props;
        this.meetingRoomHelper = MeetingRoomHelper.getInstance();
        window.log?.info('[RemoteShow]: start listen handlePlayerAction');

        this.registerEventHandler();
        this.props.liveManager.roomDocId = docId;
        this.props.liveManager.setMeetingRoomHelper(this.meetingRoomHelper);

        this.setAudienceDocTypeToLiveManager();
    }

    private registerEventHandler() {
        this.eventsOffCallbacks.push(
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.DataLoading, ({ loadingText }) => {
                this.props.changeLoadingText(true, loadingText);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.CancelDataLoading, () => {
                this.props.changeLoadingText(false);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.PlayerReady, (data) => {
                this.onPlayerReady(data.playerInitData);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.PlayerState, (data) => {
                this.meetingRoomHelper.playerState = {
                    playerAction: PlayerAction.PlayerState,
                    ...data,
                };
                this.setState({
                    playerState: {
                        playerAction: PlayerAction.PlayerState,
                        ...data,
                    },
                });
                if (data.scale !== undefined) {
                    this.updateScale(data);
                }
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.GotoPageFragment, (data) => {
                this.meetingRoomHelper.playerState = {
                    playerAction: PlayerAction.GotoPageFragment,
                    ...data,
                };
                document.body.click();
                this.props.liveManager.GotoPage();
                if (data.scale !== undefined) {
                    this.updateScale(data);
                }
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.EndPlay, (data) => {
                this.handleEndPlay(data.pageIndex);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.MobileOpenToolBar, () => {
                this.showMobilePlayerToolBar();
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.LinkClicked, ({ href }) => {
                MeetingRoomHelper.getInstance().disableAutoEndPlay = FullscreenHandler.getInstance().isFullscreen;
                openUrlWithCheck(getRedirectUrl(href), true);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.Error, ({ lastError }) => {
                this.props.showRoomError(lastError);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.PlayerMouseMove, (data) => {
                this.props.liveManager.onLaserPenMovedV2(data);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.AnnotationMessage, (data) => {
                this.props.liveManager.sendBoardMessage(data);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.TurnLaser, (enable) => {
                this.meetingRoomHelper.playerState.laserTurnOn = enable;
                this.setState({
                    playerState: {
                        ...this.state.playerState,
                        laserTurnOn: enable,
                    },
                });
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.TurnAnnotation, ({ enable }) => {
                this.props.liveManager.turnAnnotation(enable);
                this.meetingRoomHelper.playerState.enableAnnotation = enable;
                this.setState({
                    playerState: {
                        ...this.state.playerState,
                        enableAnnotation: enable,
                    },
                });
            }),
            eventBus.on(EventKeys.VIDEO_FULLSCREEN_CHANGE, (isVideoFullScreen: boolean) => {
                if (!isVideoFullScreen) {
                    // 视频退出全屏时，阻止退出播放
                    MeetingRoomHelper.getInstance().disableAutoEndPlay = true;
                }
            }),

            this.props.liveManager.event.on(ClientReady, this.onMeetingRoomClientReady),
            this.props.liveManager.event.on(LiveStateChanged, this.onLiveStateChanged),
            // 观众响应演示者翻页操作
            this.props.liveManager.event.on(PageChanged, this.onMeetingRoomPageChanged),
            this.props.liveManager.event.on(AllowFlipChanged, (allow: AllowFlipState) => {
                if (allow === AllowFlipState.YES) {
                    MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteAllowFlipOn);
                } else if (allow === AllowFlipState.NO) {
                    MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteAllowFlipOff);
                }
            }),

            // 观众进入时，如果有extra，则打开批注
            this.props.liveManager.event.on(RoomNewInfoChanged, () => {
                const extra = this.props.liveManager.liveInfo.extra;
                if (!extra) return;

                const data = JSON.parse(extra);
                if (!data) return;

                MeetingEventBus.sendRoomEvent2Player(RoomAction.TurnAnnotation, {
                    enable: true,
                    initData: data,
                });
            }),

            this.props.liveManager.event.on('BoardMessage', ({ boardMessage }) => {
                console.debug('[BoardMessage]', boardMessage);
                if (!boardMessage) return;

                if (boardMessage.action === 'TURN_ANNOTATION') {
                    let data = undefined;
                    const extra = this.props.liveManager.liveInfo.extra;
                    if (extra) data = JSON.parse(extra);

                    MeetingEventBus.sendRoomEvent2Player(RoomAction.TurnAnnotation, {
                        enable: boardMessage.enable,
                        initData: data,
                    });
                } else {
                    MeetingEventBus.sendRoomEvent2Player(RoomAction.AnnotationMessage, boardMessage);
                }
            }),

            this.props.liveManager.event.on(FallbackToPolling, () => {
                Snackbar.show({
                    message: i18n.t('当前演示参会人数过多，演示信息可能延迟'),
                    type: 'info',
                    duration: 3000,
                });
            }),

            this.props.liveManager.event.on(LiveSocketShutDown, (info) => {
                window.log?.info('[LiveSocketShutDown] info:', info);
                const code = info?.code;
                CommonTips.getInstance().show({
                    message: `${i18n.t('当前网络异常, 请刷新重试')} ${code ?? ''}`,
                    commonTipsType: CommonTipsType.ERROR,
                    actionType: CommonTipsActionType.REFRESH,
                    errorCode: code,
                });
            }),
        );
    }

    // Player初始化完毕
    private onPlayerReady(playerInitData) {
        const { sceneSource } = this.props;
        window.playerInitData = playerInitData;
        if (sceneSource === MEETING_SCENE_SOURCE.InEditor) {
            this.onEditorRemotePlayerReady();
        } else if (sceneSource === MEETING_SCENE_SOURCE.MeetingRoom) {
            this.onMeetingRoomPlayerReady();
        } else {
            window.log?.error('[RemoteShow]: sceneSource error');
        }
    }

    private updateScale = (playerState: IPlayerState) => {
        this.setState({
            playerState: {
                ...this.state.playerState,
                ...playerState,
            },
        });
    };

    // 观众响应演示者翻页操作
    private onMeetingRoomPageChanged = () => {
        const { docType } = this.props;
        const serverData = this.props.liveManager.targetPosition();
        window.log?.info('[RemoteShow]: onMeetingRoomPageChanged, serverData', serverData);
        const allowFlip = this.props.liveManager.allowFlip(); // 远端是否允许观众翻页
        const localData = MeetingRoomHelper.getInstance().playerState;
        let currentPage: number;
        let currentFragment: number;
        let iaFragmentInfo: string;
        if (this.props.liveManager.role === LiveRoleType.Audience && allowFlip && localData.isManulFlipped) {
            currentPage = localData.remoteCurrentPage;
            currentFragment = localData.remoteCurrentFragment;
            iaFragmentInfo = localData.remoteIaFragmentInfo;
        } else {
            currentPage = localData.currentPage;
            currentFragment = localData.currentFragment;
            iaFragmentInfo = localData.iaFragmentInfo;
        }
        const shouldStopMainAnim = localData.shouldStopMainAnim;
        const serverStopA = (serverData as any).stopA;
        const serverStopT = (serverData as any).stopT;
        if (serverData.p !== currentPage || serverData.f !== currentFragment
            || !isEqual(iaFragmentInfo, serverData.iaF) || serverStopA !== shouldStopMainAnim) {
            const offset = docType === DOC_TYPE.SLIDE ? 0 : 1; // 兼容livecore对PPT的p、f减了1
            const isLoadPageBySwitchForward = !!(serverData as any).prev;
            MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteChangePage, {
                currentPage: serverData.p + offset,
                currentFragment: serverData.f + offset,
                iaFragmentInfo: serverData.iaF,
                shouldStopMainAnim: serverStopA,
                shouldStopTransition: serverStopT,
                isLoadPageBySwitchForward,
            });
        }
    };

    private onLiveStateChanged = (args: [LiveState, string]) => {
        const state = args[0] || 0;
        switch (state) {
            case LiveState.OnAir:
                this.handlerLiveStateOnAir();
                break;
            case LiveState.Pause:
                this.handlerLiveStatePause();
                break;
            case LiveState.Ended:
                this.handlerLiveStateEnd();
                break;
            default:
                this.handlerLiveStateDefault(args);
                break;
        }
    };

    private handlerLiveStateOnAir = () => {
        const { role, scenceEditorRemote, meetingRoomReport } = this.props;

        // 二次 OnAir，即主讲人暂停后恢复
        if (this.hasLiveBeenOnAir) {
            if (this.props.role === ROLE.AUDIENCE) {
                this.showPlayerFrame();
            }
            return;
        }

        // 首次 OnAir
        this.hasLiveBeenOnAir = true;
        window.log?.info('[RemoteShow]: onLiveStateChanged:LiveState.OnAir');
        if (role === ROLE.AUDIENCE) {
            // 观众开始显示
            meetingRoomReport.reportPerformanceNowNotEditor(scenceEditorRemote, 'liveStateOnAir', true);
            window.log?.info('[RemoteShow]: LiveState.OnAir,this.role === ROLE.AUDIENCE');
            this.taskPrepareSocket.resolve();
        }
        tdw({
            opername: 'doc_ppt',
            module: 'remote_watch',
            action: 'en_play',
            ver10: 'new_menubar',
        });
    };

    private handlerLiveStatePause = () => {
        const { role, meetingRoomStateChange, scenceEditorRemote, meetingRoomReport } = this.props;
        window.log?.info('[RemoteShow]: onLiveStateChanged:LiveState.Pause');
        if (role === ROLE.AUDIENCE) {
            meetingRoomReport.reportPerformanceNowNotEditor(scenceEditorRemote, 'liveStatePause', true);
            window.log?.info('[RemoteShow]: LiveState.Pause,this.role === ROLE.AUDIENCE');
            meetingRoomStateChange(MEETING_ROOM_ACTION.AUDIENCE_WAIT);
        }
    };

    private handlerLiveStateEnd = () => {
        const { showRoomClose, scenceEditorRemote, meetingRoomReport } = this.props;
        window.log?.info('[RemoteShow]: onLiveStateChanged:LiveState.Ended');
        meetingRoomReport.reportPerformanceNowNotEditor(scenceEditorRemote, 'liveStateEnd', true);
        this.handleEndPlay();
        showRoomClose();
    };

    private handlerLiveStateDefault = (args: [LiveState, string]) => {
        const { showRoomClose, scenceEditorRemote, meetingRoomReport } = this.props;
        // 已知情况:房间过期，登录失败，返回了:args = [0,"login fail  errMsg, code 70010"]
        const reason = args[1] || '';
        window.log?.error('[RemoteShow]: onLiveStateChanged:default', reason);
        meetingRoomReport.reportPerformanceNowNotEditor(scenceEditorRemote, 'liveStateDefault', true);
        // socket 传来的未知消息，目前只有房间过期，房间进入关闭页面
        showRoomClose();
    };

    private handleEndPlay = (pageIndex?: number) => {
        window.log?.info('[RemoteShow]: handleEndPlay');
        const { scenceEditorRemote, handleScenceEditorEndPlay, showRoomClose } = this.props;
        if (scenceEditorRemote) {
            handleScenceEditorEndPlay?.(pageIndex);
            this.props.liveManager.liveEnd();
        } else {
            window.location.hash = '';
            showRoomClose();
            this.props.liveManager.liveEnd();
            if (FullscreenHandler.getInstance().isFullscreen) {
                FullscreenHandler.getInstance().exitFullscreen();
            }
        }
    };

    private handleFullScreenChangeForAllBrowser = (listenFunction: (...args: any[]) => void, callback: () => void) => {
        [
            'fullscreenchange',
            'mozfullscreenchange',
            'webkitfullscreenchange',
            'msfullscreenchange',
        ].map(event => listenFunction(event, callback));
    };

    private fullScreenChange = () => {
        // 与 player 点击链接跳转行为相同，player中有全屏处理逻辑会导致退出全屏时触发结束播放；
        // 引发bug，此行目的仅做flag设置，避免player结束播放，具体逻辑请看src/framework/player/src/components/manager.js
        // 里的 on linkClicked，和fullScreenChange里的endPlay
        eventBus.emit(EventKeys.LINK_CLICKED);
        const { isFullscreen } = FullscreenHandler.getInstance();
        window.log?.info('[RemoteShow]: fullScreenChange');
        this.setState({ isFullscreen });

        if (!isFullscreen) {
            if (this.props.liveManager.role === LiveRoleType.Audience
                || MeetingRoomHelper.getInstance().disableAutoEndPlay) {
                // 观众不会退出播放态
                // 手工通过按钮调整，不会退出播放态
            } else {
                const { currentPage } = this.state.playerState;
                MeetingEventBus.sendRoomEvent2Player(RoomAction.ExitShow);
                this.handleEndPlay(currentPage);
            }
        }
        MeetingRoomHelper.getInstance().disableAutoEndPlay = false;
    };

    private renderLaserBoard() {
        const { docType, role } = this.props;
        const { playerState } = this.state;

        return docType === DOC_TYPE.SLIDE
            ? null
            : <LaserBoardContainer
                playerState={playerState}
                isMaster={role === ROLE.MASTER}
                liveManager={this.props.liveManager}
            />;
    }

    private renderLiveBar() {
        const { docType, padId, role } = this.props;
        const { playerState } = this.state;
        const offset = docType === DOC_TYPE.PDF ? -1 : 0; // 兼容pdf观看者侧currentPage未从0开始
        const currentPage = playerState.currentPage + offset;
        return <LiveBar
            currentPage={currentPage}
            playerState={playerState}
            liveManager={this.props.liveManager}
            docType={docType}
            padId={padId}
            role={role}
            isFullScreen={this.state.isFullscreen}
        />;
    }

    private syncWithMaster() {
        MeetingEventBus.sendRoomEvent2Player(RoomAction.SyncWithMaster);
    }

    private showMobilePlayerToolBar = () => {
        const toolbar = document.getElementById('playerToolBarMobile');
        if (toolbar) {
            this.showPlayerToolBar(toolbar);
            if (this.mobileToolBarHiddenTimer) {
                clearTimeout(this.mobileToolBarHiddenTimer);
                this.mobileToolBarHiddenTimer = null;
            }
            this.mobileToolBarHiddenTimer = setTimeout(() => {
                this.mobileToolBarHiddenTimer = null;
                this.hidePlayerToolBar(toolbar);
            }, 3000);
        }
    };

    private hidePlayerToolBar = (toolbar) => {
        if (this.isSlide()) {
            toolbar.style.display = 'none';
        } else {
            toolbar.style.opacity = '0';
        }
    };

    private showPlayerToolBar = (toolbar) => {
        if (this.isSlide()) {
            toolbar.style.display = 'flex';
        } else {
            toolbar.style.opacity = '1';
        }
    };

    private setAudienceDocTypeToLiveManager = () => {
        const { role, docType } = this.props;
        if (role === ROLE.AUDIENCE) {
            this.props.liveManager.setDocType(docType);
        }
    };

    /** 等待前序准备任务完成后展示 player */
    private async waitForShowPlayer() {
        await Promise.all([this.taskPreparePlayer, this.taskPrepareSocket]);

        this.sendLiveBegin();
        this.onMeetingRoomPageChanged();
        this.showPlayerFrame();

        if (this.props.role === ROLE.MASTER && ua.isPC) {
            // 打开激光笔
            MeetingEventBus.sendRoomEvent2Player(RoomAction.LaserTrunOn);
        } else {
            // 观众成功进入了房间
            MeetingEventBus.sendRoomEvent2Player(RoomAction.BeAudience);
        }
    }

    private isSlide = () => !ua.isTencentMeeting && this.props.docType !== DOC_TYPE.DOC;

    /** 脱离跟随提示 */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private showEndFollowTips = once(() => {
        const ONE_DAY = 1000 * 60 * 60 * 24;
        const lastShowTimestamp = userLocalStorage.getItem(LocalStorageKeys.STORAGE);

        if (lastShowTimestamp && Date.now() - +lastShowTimestamp < ONE_DAY) return;

        Snackbar.show({
            message: i18n.t('你可以自由浏览此文档'),
            type: 'success',
            duration: 3000,
        });
        userLocalStorage.setItem(LocalStorageKeys.STORAGE, Date.now());
    });

    private getDirectionDegree = (localPage: number, remotePage: number) => {
        if (localPage < remotePage) return 180;
        if (localPage > remotePage) return 0;

        return undefined;
    };

    private renderFollowBtn = () => {
        if (this.props.role === ROLE.MASTER || !this.props.liveManager.allowFlip()) return null;
        const { currentPage = 0 } = this.state.playerState;

        // NOTE: this.props.currentPage 是 index, 而 liveDelegate.liveInfo.page 为实际页数
        const localPage = currentPage + 1;
        const remotePage = this.props.liveManager.liveInfo.page;

        const visiable = this.props.liveManager.isManulFlipped;
        visiable && this.showEndFollowTips();
        return (
            <FollowBtn
                visiable={visiable}
                shouldShowGuideAnimation
                guideText={i18n.t('跟随共享人')}
                tipText={i18n.t('跟随共享人')}
                imgUrl={this.props.liveManager.masterAvatar ?? APP_OFFICIAL_ACCOUNT}
                directionDegree={this.getDirectionDegree(localPage, remotePage)}
                onClick={this.syncWithMaster}
            />
        );
    };
}
